import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { Aplazador, esDia, UltimaRespuesta } from 'src/app/utils/rango-fechas';
import { guardarArchivo } from 'src/app/utils/descargar';

/**
 * CONSULTAS → CUADRE DEL DÍA (CCP-10..12; Mario 4-nov, maqueta ~/Documents/reportes-cuentas-ct/preview.html). El cálculo de la liquidación
 * de un cierre, paso a paso, en Bolívares a la tasa del cierre (LOT-03), con el «Asiento sugerido» al lado del «Asiento registrado» y su
 * diferencia. Lo registra el tesorero (ASC-02); este reporte solo lo propone. Todo viene calculado del núcleo
 * (GET /liquidacion/cierres/:dia/cuadre-liquidacion): aquí solo se formatea.
 */
const DIA_MS = 86400000;

@Component({
  selector: 'app-liquidacion-cuadre-dia',
  templateUrl: './liquidacion-cuadre-dia.component.html'
})
export class LiquidacionCuadreDiaComponent implements OnInit, OnDestroy {
  f = { dia: '', comercio: '' };
  r: any = null;
  comercios: string[] = [];
  cargando = false;
  error = '';
  hoy = '';
  bajando = { excel: false, pdf: false };
  readonly esqueleto = [0, 1, 2, 3];
  readonly filasGris = [0, 1, 2, 3, 4];

  avisoFecha = '';
  readonly cancelar$ = new Subject<void>();
  private ultima = new UltimaRespuesta(() => this.cancelar$.next());
  private aplazador = new Aplazador();

  constructor(private liq: LiquidacionService) { }

  ngOnInit(): void {
    this.hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(new Date());
    this.f.dia = new Date(Date.parse(`${this.hoy}T00:00:00Z`) - DIA_MS).toISOString().slice(0, 10);
    void this.buscar();
  }
  ngOnDestroy(): void { this.aplazador.cancelar(); this.ultima.cancelarTodo(); }

  /** Filtro nuevo: se vuelve al gris y la respuesta anterior, si llega tarde, se descarta. */
  cambiarFiltro = (): void => {
    if (!esDia(this.f.dia)) { this.avisoFecha = 'Completa la fecha (día, mes y año) para consultar.'; this.aplazador.cancelar(); return; }   // un día vacío no se consulta
    this.avisoFecha = ''; this.r = null;
    this.aplazador.en(400, () => { void this.buscar(); });
  };

  buscar = async (): Promise<void> => {
    if (!esDia(this.f.dia)) return;
    const n = this.ultima.nuevo();   // cancela la petición anterior
    this.cargando = true; this.error = '';
    try {
      const r = await this.liq.cuadreLiquidacion(this.f.dia, this.f.comercio, this.cancelar$);
      if (!this.ultima.vigente(n)) return;
      this.r = r;
      if (Array.isArray(r.comercios_en_cierre) && r.comercios_en_cierre.length) this.comercios = r.comercios_en_cierre;
    } catch (e: any) { if (this.ultima.vigente(n)) { this.error = e?.error?.msg || 'No se pudo leer el cuadre de ese día.'; this.r = null; } }
    finally { if (this.ultima.vigente(n)) this.cargando = false; }
  };

  // ── descargas (opcionales) ──
  private nombre = (ext: string): string => `cuadre-liquidacion-${this.f.dia.replace(/-/g, '')}${this.f.comercio ? `-${this.f.comercio}` : ''}.${ext}`;
  bajarExcel = async (): Promise<void> => {
    this.bajando.excel = true; this.error = '';
    try { guardarArchivo(await this.liq.cuadreLiquidacionExcel(this.f.dia, this.f.comercio), this.nombre('xlsx')); }
    catch (e: any) { this.error = await this.mensaje(e, 'No se pudo bajar el Excel.'); }
    finally { this.bajando.excel = false; }
  };
  bajarPdf = async (): Promise<void> => {
    this.bajando.pdf = true; this.error = '';
    try { guardarArchivo(await this.liq.cuadreLiquidacionPdf(this.f.dia, this.f.comercio), this.nombre('pdf'), 'application/pdf'); }
    catch (e: any) { this.error = await this.mensaje(e, 'No se pudo bajar el PDF.'); }
    finally { this.bajando.pdf = false; }
  };
  private async mensaje(e: any, por_defecto: string): Promise<string> {
    try { return JSON.parse(await e?.error?.text()).msg || por_defecto; } catch (x) { return e?.error?.msg || por_defecto; }
  }

  // ── formato: Bs primero, «—» sin tasa ──
  private num = (v: number, d = 2): string => Number(v).toLocaleString('es', { minimumFractionDigits: d, maximumFractionDigits: d });
  fb = (bs: number | null | undefined): string => (bs === null || bs === undefined ? '—' : `Bs ${this.num(bs)}`);
  fu = (usd: number | null | undefined): string => (usd === null || usd === undefined ? '—' : `USD ${this.num(usd)}`);
  nn = (v: number | null | undefined): string => this.num(Number(v) || 0, 0);
  pc = (v: number | null | undefined): string => `${this.num(Number(v) || 0, 1)} %`;
  corto = (dia: string): string => (dia ? dia.split('-').reverse().join('/') : '');
  /** «−» o «+» que el núcleo manda en el asiento. */
  signo = (s: string): string => (s === '-' ? '−' : '+');
  /** Cantidad de lotes: el núcleo manda un número, o la lista. */
  get nLotes(): number { return Array.isArray(this.r?.lotes) ? this.r.lotes.length : Number(this.r?.lotes) || 0; }
  get hayPuente(): boolean { return !!this.r?.puente_mt && typeof this.r.puente_mt === 'object'; }
}
